import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  InteractionObjectType,
  InteractionType,
  OrganisationTypeConstants,
  ProjectActivitiesConstants,
  ProjectPermissionConstants,
  ProjectStatusConstants,
  UserType,
} from "../../constants";
import { PillData } from "../../models";
import { CarouselActivity } from "../../models/shared/carouselActivityType";
import { isActivityDataValid } from "../../route/developer/activities";
import { ProjectContext } from "../../route/shared/projects/project/ProjectContext";
import { createActivityDraft, resumeActivity, versionConflictModalGotDisplayed } from "../../route/shared/utils";
import {
  getActivityHistoryDetails,
  searchActivities,
  searchActivityHistory,
  SearchActivityHistoryResponse,
  searchInteractions,
} from "../../service/query";
import { Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { getActivityViewRoute, getProjectAuditHistoryRoute, getPublicProjectOverviewRoute } from "../../utils/routes";
import { dataGridMapFilterCriteria, ProfileMenuItemType } from "../../widget";
import toast from "../../widget/general/Toast/Toast";

interface UseDeveloperProjectCardWithTabsTemplateReturnData {
  quickActionsMenuOptionsList: ProfileMenuItemType[];
  setShowQuickActionMenu: Dispatch<SetStateAction<boolean>>;
  showQuickActionMenu: boolean;
  setShowMergeProjectsModal: Dispatch<SetStateAction<boolean>>;
  showMergeProjectsModal: boolean;
  setShowPublishProjectModal: Dispatch<SetStateAction<boolean>>;
  showPublishProjectModal: boolean;
  setShowDeleteProjectModal: Dispatch<SetStateAction<boolean>>;
  showDeleteProjectModal: boolean;
  setShowMultiDraftModal: Dispatch<SetStateAction<boolean>>;
  showMultiDraftModal: boolean;
  setShowVersionConflictModal: Dispatch<SetStateAction<boolean>>;
  showVersionConflictModal: boolean;
  setShowNotesPanel: Dispatch<SetStateAction<boolean>>;
  showNotesPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;
  showUpdateStatusModal: boolean;
  setShowUpdateStatusModal: Dispatch<SetStateAction<boolean>>;
  showCancelProjectModal: boolean;
  setShowCancelProjectModal: Dispatch<SetStateAction<boolean>>;
  showTagsPanel: boolean;
  draftActivityHistory?: SearchActivityHistoryResponse;
  newVersionActivityHistory?: SearchActivityHistoryResponse;
  draftActivityHistoryUuid?: string;
  carouselActivities?: CarouselActivity[];
  tags: PillData[];
  navigateToDraftActivity: () => void;
  resumeDraft: (uuid: string, draftActivityHistory: SearchActivityHistoryResponse) => void;
  isExternalProject: () => boolean;
  hasProjectPermission: (permission: string) => boolean;
  shouldRefreshActivities: () => void;
  shouldRefreshProjectDetails: () => void;
  shouldRefreshTags: () => void;
  currentUserType: UserType;
  isGroupProject: boolean;
}

export const useDeveloperProjectCardWithTabsTemplate = (
  projectStatus: string | undefined,
  projectUuid: string | undefined
): UseDeveloperProjectCardWithTabsTemplateReturnData => {
  const navigate = useNavigate();

  const { currentUserType, currentOrganisationUuid } = useAuth();
  const {
    projectDetails,
    isExternalProject,
    hasProjectPermission,
    refreshActivities,
    shouldRefreshActivities,
    shouldRefreshProjectDetails,
  } = useContext(ProjectContext);

  const [showQuickActionMenu, setShowQuickActionMenu] = useState(false);
  const [showMergeProjectsModal, setShowMergeProjectsModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [showPublishProjectModal, setShowPublishProjectModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showCancelProjectModal, setShowCancelProjectModal] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [carouselActivities, setCarouselActivities] = useState<CarouselActivity[]>();
  const [showMultiDraftModal, setShowMultiDraftModal] = useState(false);
  const [draftActivityHistory, setDraftActivityHistory] = useState<SearchActivityHistoryResponse>();
  const [hasFetchedActivityHistory, setHasFetchedActivityHistory] = useState(false);
  const [tags, setTags] = useState<PillData[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);
  // Version conflict modal
  const [showVersionConflictModal, setShowVersionConflictModal] = useState(false);
  const [newVersionActivityHistory, setNewVersionActivityHistory] = useState<SearchActivityHistoryResponse>();
  const [draftActivityHistoryUuid, setDraftActivityHistoryUuid] = useState<string>();

  const quickActionsMenuOptionsList: ProfileMenuItemType[] = [];

  if (
    currentUserType === OrganisationTypeConstants.DEVELOPER &&
    (projectStatus === "Pre-development" || projectStatus === "Draft")
  ) {
    quickActionsMenuOptionsList.push(
      // KAN-7893
      // {
      //   id: 1,
      //   value: "Match with existing project",
      //   action: () => setShowMergeProjectsModal(true),
      // },
      {
        id: 1,
        value: "Delete project",
        action: () => setShowDeleteProjectModal(true),
      }
    );
  }

  if (currentUserType === OrganisationTypeConstants.DEVELOPER && projectStatus === ProjectStatusConstants.DRAFT) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "Publish project",
      action: () => setShowPublishProjectModal(true),
    });
  }

  if (currentUserType === OrganisationTypeConstants.DEVELOPER) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: `Update ${ProjectStatusConstants.REGISTRY_STATUS_LABEL}`,
      action: () => setShowUpdateStatusModal(true),
    });
  }

  if (
    currentUserType === OrganisationTypeConstants.DEVELOPER &&
    hasProjectPermission(ProjectPermissionConstants.DELETE_PROJECT)
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "Cancel project",
      action: () => setShowCancelProjectModal(true),
    });
  }

  if (
    currentUserType === OrganisationTypeConstants.DEVELOPER &&
    (hasProjectPermission(ProjectPermissionConstants.READ_ALL_PROJECT_AUDIT) ||
      hasProjectPermission(ProjectPermissionConstants.READ_OWN_ORG_PROJECT_AUDIT))
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "View audit history",
      action: () => navigate(getProjectAuditHistoryRoute(projectUuid || "", currentUserType)),
    });
  }

  if (
    currentUserType === OrganisationTypeConstants.ASSET_MANAGER ||
    currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "View project in directory",
      action: () => navigate(getPublicProjectOverviewRoute(projectUuid || "")),
    });
  }

  if (
    currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ||
    currentUserType === OrganisationTypeConstants.DEVELOPER
  ) {
    quickActionsMenuOptionsList.push({
      id: quickActionsMenuOptionsList.length + 1,
      value: "View notes",
      action: () => setShowNotesPanel(true),
    });
  }

  const shouldRefreshTags = (): void => setRefreshTags((prev) => !prev);

  const resumeDraft = async (
    _draftActivityHistoryUuid: string,
    localDraftActivityHistory: SearchActivityHistoryResponse
  ): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const activityHistory = localDraftActivityHistory.results.find((x) => x.uuid === _draftActivityHistoryUuid)!;

    await resumeActivity(
      navigate,
      setDraftActivityHistoryUuid,
      setNewVersionActivityHistory,
      setShowVersionConflictModal,
      activityHistory.activity.uuid,
      activityHistory.uuid,
      activityHistory.versionNumber,
      true
    );
  };

  const navigateToDraftActivity = async (): Promise<void> => {
    if (draftActivityHistory?.results) {
      if (draftActivityHistory.results.length > 1) {
        setShowMultiDraftModal(true);
      } else {
        await resumeActivity(
          navigate,
          setDraftActivityHistoryUuid,
          setNewVersionActivityHistory,
          setShowVersionConflictModal,
          draftActivityHistory.results[0].activity.uuid,
          draftActivityHistory.results[0].uuid,
          draftActivityHistory.results[0].versionNumber,
          true
        );
      }
    }
  };

  const fetchDraftActivities = useCallback(async () => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.activity = {
      project: {
        uuid: {
          operator: "eq",
          value: projectUuid,
        },
      },
    };

    filterCriteria.isDraft = {
      operator: "eq",
      value: true,
    };

    const response = await searchActivityHistory({
      activityUuid: null,
      paging: {
        afterCursor: null,
        beforeCursor: null,
        limit: 10,
      },

      filter: { results: filterCriteria },
    });

    if (response.status === Status.Success && response.data && response.data.results.length) {
      setDraftActivityHistory(response.data);
    }
  }, [refreshActivities]);

  const fetchCarouselData = useCallback(async (): Promise<void> => {
    if (!projectUuid) throw new Error("Uuid must not be empty!");
    const groupUuid = projectDetails?.group?.groupUuid;
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.status = {
      operator: "nin",
      value: ProjectActivitiesConstants.COMPLETED_STATUSES,
    };

    let groupFilterCriteria = {};
    if (groupUuid) {
      groupFilterCriteria = {
        filterGroups: [
          {
            operator: "or",
            filter: {
              results: {
                project: {
                  uuid: {
                    operator: "eq",
                    value: projectUuid,
                  },
                },
                group: {
                  uuid: {
                    operator: "eq",
                    value: groupUuid,
                  },
                },
              },
            },
          },
        ],
      };
    } else {
      filterCriteria.project = {
        uuid: {
          operator: "eq",
          value: projectUuid,
        },
      };
    }
    const response = await searchActivities({
      ...{
        paging: {
          limit: 3,
          beforeCursor: null,
          afterCursor: null,
        },
        sort: [
          {
            key: "results.latestVersion.updatedAt",
            direction: "desc",
          },
        ],
        filter: { results: filterCriteria },
      },
      ...groupFilterCriteria,
    });

    if (response.status === Status.Success && response.data && response.data.results) {
      const mappedActivities = response.data.results.map((activity) => ({
        uuid: activity.uuid,
        projectUuid: activity.project?.uuid,
        groupUuid: activity.group?.uuid || undefined,
        activityDefinitionUuid: activity.activityDefinition.uuid,
        activityHistoryUuid:
          activity?.draftVersion?.uuid || activity?.currentVersion?.uuid || activity?.reviewVersion?.uuid,
        activityHistoryRowVersion: activity?.draftVersion ? activity?.draftVersion.rowVersion : undefined,
        isUnderReview: activity.isUnderReview,
        isDraftVersion: !!activity?.draftVersion,
        isCurrentVersion: activity?.draftVersion ? false : !!activity?.currentVersion,
        rowVersion: activity.rowVersion,
        status: activity.status,
        verifierDisplayName:
          activity.project?.verifier?.displayName || activity.group?.verifier?.displayName || undefined,
        displayName: activity?.draftVersion
          ? `${activity.activityDefinition.displayName} (Draft)`
          : activity.activityDefinition.displayName,
        completionPercentage: Math.round(
          // eslint-disable-next-line no-nested-ternary
          activity.draftVersion?.completionPercentage
            ? activity.draftVersion.completionPercentage * 100
            : // eslint-disable-next-line no-nested-ternary
              activity.currentVersion?.completionPercentage
              ? activity.currentVersion.completionPercentage * 100
              : activity.reviewVersion != null
                ? 100
                : 0
        ),
        // eslint-disable-next-line no-nested-ternary
        isManuallyApproved: activity.currentVersion
          ? activity.currentVersion?.activityDefinitionVersion?.isManuallyApproved
          : activity.draftVersion
            ? activity.draftVersion?.activityDefinitionVersion?.isManuallyApproved
            : activity.reviewVersion == null,
        supportsDigitalVerification:
          activity.project?.verifier?.supportsDigitalVerification ??
          activity.group?.verifier?.supportsDigitalVerification ??
          undefined,
        isActivityDataValid: false,
        isVersionConflict: async () => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const activityHistory = draftActivityHistory!.results.find((x) => x.uuid === activity?.draftVersion?.uuid)!;

          return versionConflictModalGotDisplayed(
            setDraftActivityHistoryUuid,
            setNewVersionActivityHistory,
            setShowVersionConflictModal,
            activityHistory.activity.uuid,
            activityHistory.uuid,
            activityHistory.versionNumber
          );
        },
        onEdit: () =>
          // eslint-disable-next-line no-nested-ternary
          activity?.draftVersion && draftActivityHistory
            ? resumeDraft(activity.draftVersion.uuid, draftActivityHistory)
            : activity?.currentVersion
              ? createActivityDraft(activity.uuid, true, navigate)
              : null,
        onView: () =>
          // eslint-disable-next-line no-nested-ternary
          activity?.currentVersion
            ? navigate(
                getActivityViewRoute(activity.currentVersion.uuid, currentUserType, `projectUuid=${projectUuid}`)
              )
            : activity?.reviewVersion
              ? navigate(
                  getActivityViewRoute(activity.reviewVersion.uuid, currentUserType, `projectUuid=${projectUuid}`)
                )
              : null,
        onResume: () =>
          // eslint-disable-next-line no-nested-ternary
          activity?.draftVersion && draftActivityHistory
            ? resumeDraft(activity.draftVersion.uuid, draftActivityHistory)
            : activity?.currentVersion
              ? createActivityDraft(activity.uuid, true, navigate)
              : toast.error({
                  message: `This activity has not yet been published. Once ${activity.createdByUser.fullName} has published the activity, you can begin editing.`,
                }),
        onDownload: () => null,
      }));

      // Refactor opportunity
      // We need to fetch the activity data for each activity in order to compute the isActivityDataValid
      // would be better to return it from the BE
      for (let index = 0; index < response.data.results.length; index++) {
        const activityHistoryUuid =
          response.data.results[index]?.draftVersion?.uuid || response.data.results[index]?.currentVersion?.uuid;

        if (activityHistoryUuid) {
          // eslint-disable-next-line no-await-in-loop
          const historyDetailsRes = await getActivityHistoryDetails({ activityHistoryUuid });

          if (historyDetailsRes.status === Status.Success && historyDetailsRes.data) {
            mappedActivities[index].isActivityDataValid = isActivityDataValid(historyDetailsRes.data.data.steps);
          }
        }
      }

      setCarouselActivities(mappedActivities);
    }
  }, [draftActivityHistory]);

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      }).then((response) => {
        if (response.data?.results != null) {
          setTags(
            response.data.results.map(
              (x) =>
                ({
                  key: x.uuid,
                  label: JSON.parse(x.data).name,
                }) as PillData
            )
          );
        }
      });
    },
    [projectUuid, currentOrganisationUuid, refreshTags]
  );

  useEffect(() => {
    fetchDraftActivities().finally(() => setHasFetchedActivityHistory(true));
  }, [fetchDraftActivities, refreshActivities]);

  useEffect(() => {
    if (hasFetchedActivityHistory) {
      fetchCarouselData().finally(() => setHasFetchedActivityHistory(false));
    }
  }, [fetchCarouselData, hasFetchedActivityHistory]);

  useEffect(() => {
    if (
      (currentUserType === OrganisationTypeConstants.DEVELOPMENT_MANAGER ||
        currentUserType === OrganisationTypeConstants.DEVELOPER) &&
      projectUuid &&
      currentOrganisationUuid
    ) {
      fetchTags(projectUuid, currentOrganisationUuid);
    }
  }, [currentUserType, projectUuid, currentOrganisationUuid, refreshTags]);

  return {
    quickActionsMenuOptionsList,
    setShowQuickActionMenu,
    showQuickActionMenu,
    setShowMergeProjectsModal,
    showMergeProjectsModal,
    setShowPublishProjectModal,
    showPublishProjectModal,
    setShowDeleteProjectModal,
    showDeleteProjectModal,
    setShowMultiDraftModal,
    showMultiDraftModal,
    setShowVersionConflictModal,
    showVersionConflictModal,
    showNotesPanel,
    setShowNotesPanel,
    showTagsPanel,
    setShowTagsPanel,
    showUpdateStatusModal,
    setShowUpdateStatusModal,
    showCancelProjectModal,
    setShowCancelProjectModal,
    draftActivityHistory,
    newVersionActivityHistory,
    draftActivityHistoryUuid,
    navigateToDraftActivity,
    carouselActivities,
    tags,
    resumeDraft,
    isExternalProject,
    hasProjectPermission,
    shouldRefreshActivities,
    shouldRefreshProjectDetails,
    shouldRefreshTags,
    currentUserType,
    isGroupProject: projectDetails?.group?.groupUuid != null,
  };
};
